import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { NonSigFieldDetails } from '@aline/common';
import { EditingMode, isInputFieldElement, Select } from '@aline/common';
import { InputLabel, Typography } from '@mui/material';
import Papa from 'papaparse';
import { Editor, Node } from 'slate';
import { AlineInput } from 'src/AlineInput';
import type { PanelVariableType, UserRoleOption, WorkflowAssignmentValue } from 'src/components';
import {
  AlineButton,
  AlineTextarea,
  AlineTooltip,
  AnswerOptionsList,
  DefaultAnswer,
  FieldRecipientAutoComplete,
  getVariableVariantDisplayString,
  usePDFFieldAPI,
  VariableTypeIcon,
  workflowStepsDialogOpenVar,
} from 'src/components';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import { IntegrationFieldAlert } from 'src/editor/toolbars/right-panel/integrations';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import {
  getFieldLabelFromVariant,
  useAuthorizedTransforms,
  useGetCurrentDoc,
  useGetLocalFieldInfo,
  useOnChange,
} from 'src/hooks';
import { useMaybeEditor } from 'src/layout-engine';
import { alineLinkButtonStyles } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';

import {
  reconcileAnswerOptionsWithServer,
  withStableAnswerOptionIds,
} from './answer-option-upsert';
import { CheckboxRequiredBoundsForm } from './CheckboxRequiredBoundsForm';
import { ToggleRow } from './ToggleRow';

export type AssignmentOption = WorkflowAssignmentValue | UserRoleOption | null;

type VariableOption = {
  options: Apollo.AnswerOptionProps[];
  id?: UUID;
};

type AnswerOptionRow = { choiceRefId: string | null; id: string; optionText: string };

const useStyles = makeStyles()(theme => ({
  csvHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  csvText: {
    ...alineLinkButtonStyles,
    fontSize: 12,
  },
  footer: {
    borderTop: `solid 1px ${theme.appColors.rule}`,
    padding: theme.spacing(1.5, 2),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  formBody: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(2),
    overflowY: 'auto',
    padding: theme.spacing(1, 2),
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
    marginBottom: theme.spacing(1),
  },
  inputLabelWithoutSpacing: {
    ...theme.appTypography.fieldLabel,
  },
  text: {
    fontSize: 14,
    margin: theme.spacing(0, 1),
  },
  variableType: {
    alignItems: 'center',
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    display: 'flex',
    height: theme.appSizes.inputHeight,
    padding: theme.spacing(0, 0.5),
  },
}));

interface VariableFormProps {
  onDone: () => void;
  variable: PanelVariableType;
  disabledReason?: string;
  formDataCy?: string;
}

export function VariableForm({
  disabledReason = '',
  formDataCy,
  onDone,
  variable,
}: VariableFormProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.form} data-cy={formDataCy}>
      <IntegrationFieldAlert variable={variable} />
      <div className={classes.formBody}>
        <div>
          <InputLabel className={classes.inputLabel}>Assignee</InputLabel>
          <VariableAssignment disabledReason={disabledReason} variable={variable} />
        </div>
        <div>
          <InputLabel className={classes.inputLabel}>Type</InputLabel>
          <VariableType variable={variable} />
        </div>
        <div>
          <InputLabel className={classes.inputLabel}>Name</InputLabel>
          <VariableName disabledReason={disabledReason} variable={variable} />
        </div>
        <VariableQuestion disabledReason={disabledReason} variable={variable} />
        <LaunchFormToggle disabledReason={disabledReason} variable={variable} />
        <OptionsForm disabledReason={disabledReason} variable={variable} />
      </div>
      {onDone && (
        <div className={classes.footer}>
          <AlineButton fullWidth color="primary" variant="contained" onClick={onDone}>
            Done
          </AlineButton>
        </div>
      )}
    </div>
  );
}

interface VariableTypeProps {
  variable: PanelVariableType;
}

function VariableType({ variable }: VariableTypeProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.variableType}>
      <VariableTypeIcon variant={variable.variant} />
      <Typography noWrap className={classes.text}>
        {getVariableVariantDisplayString(variable.variant)}
      </Typography>
    </div>
  );
}

type VariableSubcomponentProps = Pick<VariableFormProps, 'disabledReason' | 'variable'>;

function LaunchFormToggle({
  disabledReason = '',
  variable,
}: VariableSubcomponentProps): JSX.Element | null {
  const { classes } = useStyles();
  const [updateVariable] = Apollo.useUpdateDocumentVariableMutation();

  if (
    !variable.isField ||
    variable.integrationLink?.integration ||
    variable.integrationLink?.attributePath
  )
    return null;

  return (
    <div>
      <InputLabel className={classes.inputLabel}>Launch Form</InputLabel>
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div>
          <ToggleRow
            disabled={Boolean(disabledReason)}
            text="Add to launch form"
            toggle={async addToLaunchForm => {
              await updateVariable({
                variables: { input: { id: variable.id, props: { addToLaunchForm } } },
              });
            }}
            value={variable.addToLaunchForm}
          />
        </div>
      </AlineTooltip>
    </div>
  );
}

function VariableName({ disabledReason = '', variable }: VariableSubcomponentProps): JSX.Element {
  const [variableName, setVariableName] = useState(variable.label);
  const [committedLabel, setCommittedLabel] = useState(variable.label);
  const [isFocused, setIsFocused] = useState(false);
  const [updateVariable] = Apollo.useUpdateDocumentVariableMutation();
  const { updateDocumentFieldText } = useUpdateDocumentFieldText({ variable });

  if (variable.label !== committedLabel) {
    setCommittedLabel(variable.label);
    if (!isFocused) setVariableName(variable.label);
  }

  const debouncedUpdate = useDebouncedCallback(async (label: string) => {
    const previousLabel = variable.label;
    const { data, error } = await updateVariable({
      variables: { input: { id: variable.id, props: { label } } },
    });
    if (!error && data) {
      updateDocumentFieldText({
        newLabel: data.updateVariable.label,
        previousLabel,
      });
    }
  }, 1000);

  useEffect(() => () => debouncedUpdate.flush(), [debouncedUpdate]);

  return (
    <AlineTooltip arrow placement="top" title={disabledReason}>
      <div>
        <AlineInput
          hideHelperText
          data-cy={Select.document.editor.rightDrawer.fieldDetailPanel.labelInput}
          disabled={Boolean(disabledReason)}
          placeholder="Add variable name..."
          value={variableName}
          onBlur={() => {
            setIsFocused(false);
            debouncedUpdate.flush();
          }}
          onChange={e => {
            setVariableName(e.target.value);
            debouncedUpdate(e.target.value);
          }}
          onFocus={() => setIsFocused(true)}
        />
      </div>
    </AlineTooltip>
  );
}

function VariableQuestion({
  disabledReason = '',
  variable,
}: VariableSubcomponentProps): JSX.Element {
  const { classes } = useStyles();
  const [question, setQuestion] = useState(variable.question);
  const [committedQuestion, setCommittedQuestion] = useState(variable.question);
  const [isFocused, setIsFocused] = useState(false);
  const [updateVariable] = Apollo.useUpdateDocumentVariableMutation();

  if (variable.question !== committedQuestion) {
    setCommittedQuestion(variable.question);
    if (!isFocused) setQuestion(variable.question);
  }

  const debouncedUpdate = useDebouncedCallback(async (nextQuestion: string) => {
    await updateVariable({
      variables: { input: { id: variable.id, props: { question: nextQuestion } } },
    });
  }, 1000);

  useEffect(() => () => debouncedUpdate.flush(), [debouncedUpdate]);

  return (
    <div>
      <InputLabel className={classes.inputLabel}>Question</InputLabel>
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div>
          <AlineInput
            hideHelperText
            data-cy={Select.document.editor.rightDrawer.fieldDetailPanel.questionInput}
            disabled={Boolean(disabledReason)}
            placeholder="Add question..."
            value={question}
            onBlur={() => {
              setIsFocused(false);
              debouncedUpdate.flush();
            }}
            onChange={e => {
              setQuestion(e.target.value);
              debouncedUpdate(e.target.value);
            }}
            onFocus={() => setIsFocused(true)}
          />
        </div>
      </AlineTooltip>
    </div>
  );
}

function VariableAssignment({ disabledReason, variable }: VariableSubcomponentProps): JSX.Element {
  const [updateVariable] = Apollo.useUpdateDocumentVariableMutation();

  return (
    <FieldRecipientAutoComplete
      autoSelectFirstRecipient={false}
      disabled={Boolean(disabledReason)}
      value={variable.action}
      onChange={async option => {
        if (!option) return;

        const isNewRecipient = option.__typename === 'NewFieldRecipient';
        if (isNewRecipient) return workflowStepsDialogOpenVar(true);

        await updateVariable({
          variables: { input: { id: variable.id, props: { actionId: option.id } } },
        });
      }}
    />
  );
}

interface OptionsFormProps extends Pick<VariableFormProps, 'disabledReason'> {
  variable: PanelVariableType;
}

function OptionsForm({ disabledReason = '', variable }: OptionsFormProps): JSX.Element | null {
  const { classes } = useStyles();
  const [csvOpen, setCSVOpen] = useState(false);
  const [copyPaste, setCopyPaste] = useState('');
  const [defaultAnswer, setDefaultAnswer] = useState<UUID | number | null>(
    variable.defaultAnswer?.id ?? null
  );
  const [minRequired, setMinRequired] = useState(variable.minRequired ?? 0);
  const [maxRequired, setMaxRequired] = useState<number | null>(variable.maxRequired ?? null);
  const syncCheckboxOptionsAfterSave = useSyncCheckboxOptionsAfterSave(variable);
  const { setValue, updateVariable, updateVariableOptions, value } = useOptionsForm({ variable });
  const isCheckboxVariant = variable.variant === Apollo.MetadataVariant.Checkbox;
  const lastSyncedCheckboxOptionsRef = useRef<AnswerOptionRow[]>(
    toAnswerOptionRows(variable.answerOptions?.options ?? [])
  );
  const serverAnswerOptionsRef = useRef<Apollo.AnswerOptionProps[]>(
    toAnswerOptionProps(variable.answerOptions?.options ?? [])
  );
  // Ignore stale debounced saves when add/delete or a newer edit invalidates them.
  const optionsSaveGenerationRef = useRef(0);
  const serverOptionIdsKey =
    variable.answerOptions?.options?.map(option => option.id ?? '').join(',') ?? '';
  const serverOptionsFromVariable = useMemo(
    () => toAnswerOptionProps(variable.answerOptions?.options ?? []),
    // Resync when option ids change; option text edits are owned by local form state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serverOptionIdsKey]
  );

  useEffect(() => {
    serverAnswerOptionsRef.current = serverOptionsFromVariable;
    lastSyncedCheckboxOptionsRef.current = toAnswerOptionRows(serverOptionsFromVariable);
  }, [serverOptionsFromVariable]);

  const markCheckboxOptionsSynced = useCallback((options: Apollo.AnswerOptionProps[]) => {
    lastSyncedCheckboxOptionsRef.current = toAnswerOptionRows(options);
  }, []);

  const applySavedAnswerOptions = useCallback(
    (savedOptions: Apollo.AnswerOptionProps[], answerOptionsId?: UUID) => {
      const normalizedOptions = toAnswerOptionProps(savedOptions);
      serverAnswerOptionsRef.current = normalizedOptions;
      markCheckboxOptionsSynced(normalizedOptions);
      setValue(current => ({
        id: answerOptionsId ?? current.id,
        options: normalizedOptions,
      }));
    },
    [markCheckboxOptionsSynced, setValue]
  );

  const debouncedSaveCheckboxBounds = useDebouncedCallback(
    async (nextMin: number, nextMax: number | null) => {
      await updateVariable({
        variables: {
          input: { id: variable.id, props: { maxRequired: nextMax, minRequired: nextMin } },
        },
      });
    },
    1000
  );

  const debouncedUpdateOptions = useDebouncedCallback(
    async ({ generation, newOptions }: { generation: number; newOptions: VariableOption }) => {
      if (optionsSaveGenerationRef.current !== generation) return;

      const previousOptions = lastSyncedCheckboxOptionsRef.current;
      const upsertAnswerOptions = withStableAnswerOptionIds(
        newOptions,
        serverAnswerOptionsRef.current
      );
      if (optionsSaveGenerationRef.current !== generation) return;

      if (!isCheckboxVariant) {
        await updateVariable({
          variables: { input: { id: variable.id, upsertAnswerOptions } },
        });
        return;
      }

      const { data, error } = await updateVariable({
        variables: { input: { id: variable.id, upsertAnswerOptions } },
      });
      if (optionsSaveGenerationRef.current !== generation) return;
      if (!error && data) {
        const savedOptions = data.updateVariable.answerOptions?.options ?? [];
        await syncCheckboxOptionsAfterSave(previousOptions, data.updateVariable);
        if (optionsSaveGenerationRef.current !== generation) return;
        applySavedAnswerOptions(savedOptions, data.updateVariable.answerOptions?.id);
      }
    },
    1000
  );

  const debouncedParseCSV = useDebouncedCallback(
    async (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (isCheckboxVariant) {
        const previousOptions = lastSyncedCheckboxOptionsRef.current;
        const parsedOptions = parseAnswerOptionsFromCopyPaste(event.target.value, value.options);
        const optionsObj = withStableAnswerOptionIds(
          { ...value, options: parsedOptions },
          serverAnswerOptionsRef.current
        );

        setDefaultAnswer(null);
        const previousValue = value;
        setValue(optionsObj);
        const { data, error } = await updateVariable({
          variables: { input: { id: variable.id, upsertAnswerOptions: optionsObj } },
        });
        if (error) {
          setValue(previousValue);
          return;
        }
        if (data) {
          const savedOptions = data.updateVariable.answerOptions?.options ?? [];
          await syncCheckboxOptionsAfterSave(previousOptions, data.updateVariable);
          applySavedAnswerOptions(savedOptions, data.updateVariable.answerOptions?.id);
        }
        return;
      }

      const { data } = Papa.parse<string[]>(event.target.value, { delimiter: ';' });
      const newOptions: Apollo.UpsertAnswerOptionsInput['options'] = data.flatMap(options =>
        options.map(optionText => ({
          optionText,
          triggerAction: Apollo.TriggerAction.Merge,
        }))
      );
      const optionsObj: VariableOption = {
        ...value,
        options: newOptions.length
          ? newOptions
          : [{ optionText: '', triggerAction: Apollo.TriggerAction.Merge }],
      };

      setDefaultAnswer(null);
      await updateVariableOptions(optionsObj);
    },
    1000
  );

  const invalidatePendingOptionsTextSave = useCallback(() => {
    optionsSaveGenerationRef.current += 1;
    debouncedUpdateOptions.cancel();
    debouncedParseCSV.cancel();
  }, [debouncedParseCSV, debouncedUpdateOptions]);

  useEffect(
    () => () => {
      debouncedUpdateOptions.flush();
      debouncedParseCSV.flush();
      debouncedSaveCheckboxBounds.flush();
    },
    [debouncedParseCSV, debouncedSaveCheckboxBounds, debouncedUpdateOptions]
  );

  switch (variable.variant) {
    case Apollo.MetadataVariant.Checkbox:
    case Apollo.MetadataVariant.Options:
    case Apollo.MetadataVariant.RadioOptions:
      break;
    case Apollo.MetadataVariant.Text:
    case Apollo.MetadataVariant.Email:
    case Apollo.MetadataVariant.Currency:
    case Apollo.MetadataVariant.Date:
    case Apollo.MetadataVariant.Decimal:
      return null;
  }

  const listVariant =
    variable.variant === Apollo.MetadataVariant.Options
      ? Apollo.FieldVariant.Dropdown
      : variable.variant === Apollo.MetadataVariant.RadioOptions
        ? Apollo.FieldVariant.Radiobutton
        : Apollo.FieldVariant.Checkbox;

  return (
    <>
      {isCheckboxVariant && (
        <CheckboxRequiredBoundsForm
          disabled={Boolean(disabledReason)}
          maxRequired={maxRequired}
          minRequired={minRequired}
          optionCount={value.options.length}
          onMaxRequiredChange={nextMax => {
            setMaxRequired(nextMax);
            debouncedSaveCheckboxBounds(minRequired, nextMax);
          }}
          onMinRequiredChange={nextMin => {
            setMinRequired(nextMin);
            debouncedSaveCheckboxBounds(nextMin, maxRequired);
          }}
        />
      )}
      {!isCheckboxVariant && (
        <div>
          <InputLabel className={classes.inputLabel}>Default Answer</InputLabel>
          <AlineTooltip arrow placement="top" title={disabledReason}>
            <div>
              <DefaultAnswer
                disabled={Boolean(disabledReason)}
                options={value.options}
                value={defaultAnswer ?? ''}
                onChangeAnswer={async idOrIndex => {
                  const nextDefault = idOrIndex === '' ? null : idOrIndex;
                  setDefaultAnswer(nextDefault);
                  await updateVariable({
                    variables: {
                      input: {
                        id: variable.id,
                        props: { defaultAnswerId: toDefaultAnswerId(nextDefault) },
                        upsertAnswerOptions: {
                          ...value,
                          defaultAnswerIndex: toDefaultAnswerIndex(nextDefault),
                        },
                      },
                    },
                  });
                }}
              />
            </div>
          </AlineTooltip>
        </div>
      )}
      {csvOpen && (
        <div>
          <InputLabel className={classes.inputLabel}>Copy and Paste Options</InputLabel>
          <AlineTooltip arrow placement="top" title={disabledReason}>
            <div>
              <AlineTextarea
                disabled={Boolean(disabledReason)}
                maxRows={5}
                minRows={3}
                placeholder="Add a list of options separated by semicolons or line breaks..."
                value={copyPaste}
                onChange={e => {
                  setCopyPaste(e.target.value);
                  debouncedParseCSV(e);
                }}
              />
            </div>
          </AlineTooltip>
        </div>
      )}
      <div>
        <div className={classes.csvHeader}>
          <InputLabel className={classes.inputLabelWithoutSpacing}>Options</InputLabel>
          <Typography className={classes.csvText} onClick={() => setCSVOpen(!csvOpen)}>
            {csvOpen ? 'Close CSV' : 'Use CSV'}
          </Typography>
        </div>
        <AlineTooltip arrow placement="top" title={disabledReason}>
          <div>
            <AnswerOptionsList
              disabled={Boolean(disabledReason)}
              options={
                value.options.length
                  ? value.options
                  : [{ optionText: '', triggerAction: Apollo.TriggerAction.Merge }]
              }
              variant={listVariant}
              onAddOption={() => {
                invalidatePendingOptionsTextSave();
                const newOption: Apollo.AnswerOptionProps = {
                  optionText: '',
                  triggerAction: Apollo.TriggerAction.Merge,
                };
                setValue({ ...value, options: [...value.options, newOption] });
              }}
              onDeleteOption={async (newOptions, deletedOption, deletedIndex) => {
                invalidatePendingOptionsTextSave();
                const deleteGeneration = optionsSaveGenerationRef.current;

                if (isCheckboxVariant) {
                  const previousOptions = lastSyncedCheckboxOptionsRef.current;
                  const nextValue = withStableAnswerOptionIds(
                    { ...value, options: newOptions },
                    serverAnswerOptionsRef.current
                  );
                  const previousValue = value;

                  setValue(nextValue);
                  const { data, error } = await updateVariable({
                    variables: { input: { id: variable.id, upsertAnswerOptions: nextValue } },
                  });
                  if (error) {
                    setValue(previousValue);
                    return;
                  }
                  if (data && optionsSaveGenerationRef.current === deleteGeneration) {
                    const savedOptions = data.updateVariable.answerOptions?.options ?? [];
                    await syncCheckboxOptionsAfterSave(previousOptions, data.updateVariable);
                    applySavedAnswerOptions(savedOptions, data.updateVariable.answerOptions?.id);
                  }
                  return;
                }

                const nextDefault = computeDefaultAfterDelete({
                  current: defaultAnswer,
                  deletedId: deletedOption?.id,
                  deletedIndex,
                });
                const nextValue: VariableOption = { ...value, options: newOptions };
                const previousValue = value;
                const previousDefault = defaultAnswer;

                setValue(nextValue);
                setDefaultAnswer(nextDefault);

                const { error } = await updateVariable({
                  variables: {
                    input: {
                      id: variable.id,
                      props: { defaultAnswerId: toDefaultAnswerId(nextDefault) },
                      upsertAnswerOptions: {
                        ...nextValue,
                        defaultAnswerIndex: toDefaultAnswerIndex(nextDefault),
                      },
                    },
                  },
                });
                if (error) {
                  setValue(previousValue);
                  setDefaultAnswer(previousDefault);
                }
              }}
              onUpdateOption={newOptions => {
                const next: VariableOption = { ...value, options: newOptions };
                optionsSaveGenerationRef.current += 1;
                const generation = optionsSaveGenerationRef.current;
                setValue(next);
                debouncedUpdateOptions({ generation, newOptions: next });
              }}
            />
          </div>
        </AlineTooltip>
      </div>
    </>
  );
}

function useUpdateDocumentFieldText({ variable }: { variable: PanelVariableType }) {
  const editor = useMaybeEditor();
  const { document } = useGetCurrentDoc();
  const currentUserId = useCurrentUserId();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const isAlineDoc = document?.latestVersion.__typename === 'DocumentVersion';

  function updateDocumentFieldText({
    newLabel,
    previousLabel,
  }: {
    newLabel: string;
    previousLabel: string;
  }) {
    /**
     * Read live from the editor instead of debounced fieldsInJSON. Right after
     * create (Add field panel), derived state can lag, so localFieldInfo is null
     * and the first rename updates the DB label without syncing doc text — after
     * that, text !== previousLabel and later renames never catch up.
     */
    if (!isAlineDoc || !editor || !currentUserId) return;

    const fieldText = getLiveNonSigFieldText(editor, variable.nodeId);
    if (fieldText === null) return;

    const defaultLabel = getFieldLabelFromVariant(variable.variant);
    const isUnfilledPlaceholder = fieldText === previousLabel || fieldText === defaultLabel;
    if (!isUnfilledPlaceholder) return;

    AuthorizedTransforms.updateInputField(editor, {
      assigneeId: null,
      authorId: currentUserId,
      editingMode: EditingMode.EDITING,
      fieldId: variable.nodeId,
      update: { text: newLabel },
    });
  }

  return { updateDocumentFieldText };
}

function getLiveNonSigFieldText(editor: Editor, fieldId: UUID): string | null {
  for (const [field] of Editor.nodes(editor, {
    at: [],
    match: node => isInputFieldElement(node) && node.fieldId === fieldId,
    voids: true,
  })) {
    if (!isInputFieldElement(field)) continue;
    if (field.variant === Apollo.FieldVariant.Signature) return null;
    return Node.string(field);
  }

  return null;
}

function useOptionsForm({ variable }: { variable: PanelVariableType }) {
  const [updateVariable] = Apollo.useUpdateDocumentVariableMutation();
  const [value, setValue] = useState<VariableOption>(() => ({
    id: variable.answerOptions.id,
    options: variable.answerOptions.options.map(o => ({
      id: o.id,
      optionText: o.optionText,
      triggerAction: o.triggerAction,
    })),
  }));
  const serverOptionIdsKey =
    variable.answerOptions?.options?.map(option => option.id ?? '').join(',') ?? '';
  const serverOptionsFromVariable = useMemo(
    () => toAnswerOptionProps(variable.answerOptions?.options ?? []),
    // Resync when option ids change; option text edits are owned by local form state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serverOptionIdsKey]
  );

  useOnChange(() => {
    setValue(current => {
      const options = reconcileAnswerOptionsWithServer(current.options, serverOptionsFromVariable);
      if (
        options.length === current.options.length &&
        options.every((option, index) => {
          const currentOption = current.options[index];
          return (
            option.id === currentOption?.id &&
            option.optionText === currentOption?.optionText &&
            option.triggerAction === currentOption?.triggerAction
          );
        })
      ) {
        return current;
      }

      return {
        id: variable.answerOptions.id,
        options,
      };
    });
  }, [serverOptionsFromVariable, variable.answerOptions.id, variable.id]);

  async function updateVariableOptions(newOptions: VariableOption) {
    const previousValue = value;
    setValue(newOptions);
    const { error } = await updateVariable({
      variables: { input: { id: variable.id, upsertAnswerOptions: newOptions } },
    });
    if (error) setValue(previousValue);
  }

  return { setValue, updateVariable, updateVariableOptions, value };
}

function useSyncCheckboxOptionsAfterSave(variable: PanelVariableType) {
  const editor = useMaybeEditor();
  const currentUserId = useCurrentUserId();
  const documentViewer = useMaybeDocumentViewer();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const { document } = useGetCurrentDoc();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const localFieldInfo = useGetLocalFieldInfo(variable.nodeId, tabId);
  const { syncPDFCheckboxWidgetsAfterOptionsEdit } = usePDFFieldAPI(documentViewer, tabId);
  const isAlineDoc = document?.latestVersion.__typename === 'DocumentVersion';
  const variantKey = 'variant' satisfies keyof NonSigFieldDetails;

  return useCallback(
    async (
      previousOptions: AnswerOptionRow[],
      updated: Pick<PanelVariableType, 'answerOptions' | 'selectedAnswers'>
    ) => {
      if (variable.variant !== Apollo.MetadataVariant.Checkbox) return;

      const nextCheckboxRows = toAnswerOptionRows(updated.answerOptions?.options ?? []);
      if (!checkboxAnswerOptionsChanged(previousOptions, nextCheckboxRows)) return;

      if (
        isAlineDoc &&
        localFieldInfo &&
        variantKey in localFieldInfo.fieldInDocument &&
        localFieldInfo.fieldInDocument.variant !== Apollo.FieldVariant.Signature &&
        currentUserId &&
        editor
      ) {
        AuthorizedTransforms.updateInputField(editor, {
          answerOptionsInput: nextCheckboxRows.map(option => ({
            // Checkbox nodes are keyed by choiceRefId; legacyId renames ones still on the row id.
            id: option.choiceRefId ?? option.id,
            legacyId: option.id,
            optionText: option.optionText,
          })),
          assigneeId: null,
          authorId: currentUserId,
          editingMode: EditingMode.EDITING,
          fieldId: variable.nodeId,
          update: {},
        });
      }

      if (!isAlineDoc) {
        await syncPDFCheckboxWidgetsAfterOptionsEdit({
          nextOptions: nextCheckboxRows,
          previousOptions,
          selectedAnswerIds: updated.selectedAnswers?.map(answer => answer.id) ?? [],
          variableNodeId: variable.nodeId,
        });
      }
    },
    [
      currentUserId,
      editor,
      isAlineDoc,
      localFieldInfo,
      syncPDFCheckboxWidgetsAfterOptionsEdit,
      variable.nodeId,
      variable.variant,
      variantKey,
      AuthorizedTransforms,
    ]
  );
}

type DefaultAnswerValue = UUID | number | null;

function toDefaultAnswerId(value: DefaultAnswerValue): UUID | null | undefined {
  if (value === null) return null;
  if (typeof value === 'string') return value;
  return undefined;
}

function toDefaultAnswerIndex(value: DefaultAnswerValue): number | undefined {
  return typeof value === 'number' ? value : undefined;
}

function computeDefaultAfterDelete({
  current,
  deletedId,
  deletedIndex,
}: {
  current: DefaultAnswerValue;
  deletedId: UUID | null | undefined;
  deletedIndex: number;
}): DefaultAnswerValue {
  if (typeof current === 'number') {
    if (current === deletedIndex) return null;
    if (current > deletedIndex) return current - 1;
    return current;
  }
  if (deletedId && current === deletedId) return null;
  return current;
}

function toAnswerOptionProps(
  options: Array<Pick<Apollo.AnswerOptionProps, 'id' | 'optionText' | 'triggerAction'>>
): Apollo.AnswerOptionProps[] {
  return options.map(option => ({
    id: option.id,
    optionText: option.optionText,
    triggerAction: option.triggerAction,
  }));
}

/**
 * Accepts both the mutation input shape and the queried option, which also carries the
 * `choiceRefId` that checkbox nodes are keyed by.
 */
function toAnswerOptionRows(
  options: (Apollo.AnswerOptionProps & { choiceRefId?: string | null })[]
): AnswerOptionRow[] {
  return options.flatMap(option =>
    option.id
      ? [{ choiceRefId: option.choiceRefId ?? null, id: option.id, optionText: option.optionText }]
      : []
  );
}
function checkboxAnswerOptionsChanged(
  before: AnswerOptionRow[],
  after: AnswerOptionRow[]
): boolean {
  if (before.length !== after.length) return true;
  const afterTextById = new Map(after.map(option => [option.id, option.optionText]));
  if (before.some(option => afterTextById.get(option.id) !== option.optionText)) return true;
  return before.some((option, index) => option.id !== after[index]?.id);
}

function parseAnswerOptionsFromCopyPaste(
  raw: string,
  existingOptions: Apollo.AnswerOptionProps[] | undefined
): Apollo.AnswerOptionProps[] {
  const { data } = Papa.parse<string[]>(raw, { delimiter: ';', skipEmptyLines: true });
  const optionTexts = data.flatMap(row => row.filter(Boolean));

  if (!optionTexts.length) {
    return [{ optionText: '', triggerAction: Apollo.TriggerAction.Merge }];
  }

  const existingByText = new Map<string, Apollo.AnswerOptionProps[]>();
  for (const option of existingOptions ?? []) {
    if (!option.id) continue;
    const key = option.optionText.trim();
    const matches = existingByText.get(key) ?? [];
    matches.push(option);
    existingByText.set(key, matches);
  }

  const usedIds = new Set<string>();

  const parsedOptions: Apollo.AnswerOptionProps[] = optionTexts.map(optionText => {
    const existing = existingByText.get(optionText.trim())?.shift();
    if (existing?.id && !usedIds.has(existing.id)) {
      usedIds.add(existing.id);
      return {
        id: existing.id,
        optionText,
        triggerAction: existing.triggerAction ?? Apollo.TriggerAction.Merge,
      };
    }

    return {
      optionText,
      triggerAction: Apollo.TriggerAction.Merge,
    };
  });

  const leftoverOptions = (existingOptions ?? []).filter(
    option => option.id && !usedIds.has(option.id)
  );
  let leftoverIndex = 0;
  for (const parsed of parsedOptions) {
    if (parsed.id) continue;
    const fallback = leftoverOptions[leftoverIndex];
    if (!fallback?.id) break;
    leftoverIndex += 1;
    usedIds.add(fallback.id);
    parsed.id = fallback.id;
    parsed.triggerAction = fallback.triggerAction ?? parsed.triggerAction;
  }

  return parsedOptions;
}
